/**
 * Teacher → Lesson Plans  (Phase 2)
 *
 * Replaces the Phase-1 placeholder.  Two views in one screen:
 *   • Left:   list of my plans (filter by subject + status + search)
 *   • Right:  editor (create / edit) with an "AI Copilot" panel.
 *
 * The AI Copilot is a teacher-approval flow: clicking "Suggest" calls Claude
 * (via the backend), shows the draft in the editor INSIDE THE FORM, and only
 * persists when the teacher hits "Save".  AI calls are logged for audit.
 */
import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Sparkles, Plus, Copy, Trash2, Save, MessageCircleQuestion, ListChecks,
  Loader2, FileText, Search, Wand2,
} from 'lucide-react';
import { portalApi } from './teacherApi';

const fmt = (iso) => iso ? new Date(iso).toLocaleString() : '';
const STATUS_COLOR = {
  draft:     'bg-slate-100 text-slate-700',
  scheduled: 'bg-sky-100 text-sky-700',
  delivered: 'bg-emerald-100 text-emerald-700',
};

const EMPTY = {
  title: '', subject_id: '', duration_minutes: 45,
  learning_objectives: [], materials_needed: [], introduction: '',
  activities: [], assessment: '', homework: '', notes: '',
  status: 'draft', ai_generated: false, ai_log_id: null,
};

const lines = (text) => (text || '').split('\n').map(s => s.trim()).filter(Boolean);

export const TeacherLessonPlans = () => {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [filterSubject, setFilterSubject] = useState('all');
  const [picked, setPicked] = useState(null);    // current plan_id or null = new
  const [form, setForm] = useState(EMPTY);
  const [aiTopic, setAiTopic] = useState('');
  const [aiBusy, setAiBusy] = useState(false);

  const { data: subjects = [] } = useQuery({
    queryKey: ['teacher-subjects'],
    queryFn: () => portalApi('/teacher/subjects'),
  });
  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['lesson-plans', search, filterSubject],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filterSubject !== 'all') qs.set('subject_id', filterSubject);
      if (search) qs.set('q', search);
      return portalApi(`/teacher/lesson-plans${qs.toString() ? `?${qs}` : ''}`);
    },
  });

  // Default-select first subject + first plan when data loads
  useEffect(() => {
    if (!form.subject_id && subjects.length) setForm(f => ({ ...f, subject_id: subjects[0].id }));
  }, [subjects, form.subject_id]);
  useEffect(() => {
    if (picked) {
      portalApi(`/teacher/lesson-plans/${picked}`).then(setForm);
    }
  }, [picked]);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        learning_objectives: Array.isArray(form.learning_objectives) ? form.learning_objectives : lines(form.learning_objectives),
        materials_needed: Array.isArray(form.materials_needed) ? form.materials_needed : lines(form.materials_needed),
      };
      if (picked)
        return portalApi(`/teacher/lesson-plans/${picked}`, { method: 'PUT', body: JSON.stringify(body) });
      return portalApi('/teacher/lesson-plans', { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: (r) => {
      toast.success(picked ? 'Lesson plan updated' : 'Lesson plan created');
      qc.invalidateQueries({ queryKey: ['lesson-plans'] });
      if (!picked && r.id) setPicked(r.id);
    },
    onError: (e) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: async () => portalApi(`/teacher/lesson-plans/${picked}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: ['lesson-plans'] });
      setPicked(null); setForm(EMPTY);
    },
  });

  const clone = useMutation({
    mutationFn: async () => portalApi(`/teacher/lesson-plans/${picked}/clone`, { method: 'POST' }),
    onSuccess: (r) => {
      toast.success('Cloned as draft');
      qc.invalidateQueries({ queryKey: ['lesson-plans'] });
      setPicked(r.id);
    },
  });

  const runAiSuggest = async () => {
    if (!aiTopic.trim()) { toast.error('Topic required'); return; }
    if (!form.subject_id) { toast.error('Pick a subject first'); return; }
    setAiBusy(true);
    try {
      const r = await portalApi('/teacher/lesson-plans/ai/suggest', {
        method: 'POST',
        body: JSON.stringify({
          subject_id: form.subject_id, topic: aiTopic,
          duration_minutes: Number(form.duration_minutes) || 45,
        }),
      });
      setForm({
        ...EMPTY,
        subject_id: form.subject_id,
        title: `${aiTopic} (Claude draft)`,
        duration_minutes: r._duration_minutes,
        learning_objectives: r.learning_objectives || [],
        materials_needed:    r.materials_needed    || [],
        introduction:        r.introduction        || '',
        activities:          r.activities          || [],
        assessment:          r.assessment          || '',
        homework:            r.homework            || '',
        ai_generated: true,
        ai_log_id: r._ai_log_id,
        status: 'draft',
      });
      setPicked(null);   // unsaved draft
      toast.success('Claude draft loaded — edit & Save to keep');
    } catch (e) { toast.error(e.message); }
    finally { setAiBusy(false); }
  };

  const runAiQuestions = async () => {
    if (!aiTopic.trim()) { toast.error('Topic required'); return; }
    setAiBusy(true);
    try {
      const r = await portalApi('/teacher/lesson-plans/ai/questions', {
        method: 'POST',
        body: JSON.stringify({ topic: aiTopic, count: 5 }),
      });
      // Drop the questions into Activities so teacher can sculpt
      const activities = (r.questions || []).map((q, i) => ({
        name: `Discussion Q${i + 1}`, duration_minutes: 5, description: q,
      }));
      setForm(f => ({ ...f, activities: [...(f.activities || []), ...activities] }));
      toast.success(`${r.questions?.length || 0} questions added`);
    } catch (e) { toast.error(e.message); }
    finally { setAiBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="teacher-lesson-plans-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-violet-500" /> Lesson Plans <Badge variant="outline">{plans.length}</Badge>
        </h2>
        <Button size="sm" onClick={() => { setPicked(null); setForm({ ...EMPTY, subject_id: subjects[0]?.id || '' }); }}>
          <Plus className="h-4 w-4 mr-1" /> New
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* ===== List ===== */}
        <Card className="lg:col-span-1">
          <CardContent className="pt-4 space-y-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <Input className="pl-7 h-8 text-sm" placeholder="Search…"
                      value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={filterSubject} onValueChange={setFilterSubject}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All my subjects</SelectItem>
                {subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>

            <div className="max-h-[60vh] overflow-y-auto space-y-1.5">
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> :
                plans.length === 0 ? <p className="text-xs text-slate-400 text-center p-4">No plans yet.</p> :
                plans.map(p => (
                  <button key={p.id} onClick={() => setPicked(p.id)}
                          className={`w-full text-left p-2 rounded border transition ${
                            picked === p.id ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:bg-slate-50'
                          }`} data-testid={`lp-row-${p.id}`}>
                    <div className="font-medium text-sm flex items-center gap-1.5 truncate">
                      {p.ai_generated && <Sparkles className="h-3 w-3 text-violet-500 shrink-0" />}
                      {p.title}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Badge variant="outline" className={`text-[10px] ${STATUS_COLOR[p.status] || ''}`}>{p.status}</Badge>
                      <span className="text-[10px] text-slate-500 truncate">{p.subject_name}</span>
                    </div>
                  </button>
                ))
              }
            </div>
          </CardContent>
        </Card>

        {/* ===== Editor ===== */}
        <Card className="lg:col-span-2">
          <CardContent className="pt-4 space-y-3">
            {/* AI Copilot panel */}
            <div className="bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200 rounded p-3">
              <div className="flex items-center gap-2 mb-2">
                <Wand2 className="h-4 w-4 text-violet-600" />
                <h3 className="text-sm font-semibold">AI Copilot · Claude Sonnet 4.6</h3>
                <Badge variant="outline" className="text-[10px]">teacher-approval flow</Badge>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Input placeholder="Topic e.g. Photosynthesis · Pythagoras"
                        value={aiTopic} onChange={(e) => setAiTopic(e.target.value)}
                        className="flex-1 min-w-0 sm:min-w-[200px]" data-testid="lp-ai-topic" />
                <Button size="sm" onClick={runAiSuggest} disabled={aiBusy} data-testid="lp-ai-suggest">
                  {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                  Draft plan
                </Button>
                <Button size="sm" variant="outline" onClick={runAiQuestions} disabled={aiBusy}>
                  <MessageCircleQuestion className="h-3.5 w-3.5 mr-1" /> Add questions
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">
                Drafts load into the form below — nothing is saved until you click <strong>Save</strong>.
              </p>
            </div>

            {/* Form */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs">Title</Label>
                <Input value={form.title || ''} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="lp-title" />
              </div>
              <div>
                <Label className="text-xs">Subject</Label>
                <Select value={form.subject_id || ''} onValueChange={(v) => setForm({...form, subject_id: v})}>
                  <SelectTrigger data-testid="lp-subject"><SelectValue placeholder="Pick…" /></SelectTrigger>
                  <SelectContent>
                    {subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Duration (minutes)</Label>
                <Input type="number" min={5} max={300} value={form.duration_minutes || 45}
                        onChange={(e) => setForm({...form, duration_minutes: Number(e.target.value)})} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Learning objectives <span className="text-slate-400">(one per line)</span></Label>
                <textarea rows={3} className="w-full border rounded p-2 text-sm"
                          value={Array.isArray(form.learning_objectives) ? form.learning_objectives.join('\n') : (form.learning_objectives || '')}
                          onChange={(e) => setForm({...form, learning_objectives: lines(e.target.value)})}
                          data-testid="lp-objectives" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Introduction</Label>
                <textarea rows={2} className="w-full border rounded p-2 text-sm"
                          value={form.introduction || ''} onChange={(e) => setForm({...form, introduction: e.target.value})} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs flex items-center gap-1"><ListChecks className="h-3 w-3" /> Activities</Label>
                {(form.activities || []).map((a, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 mb-1">
                    <Input className="col-span-3" placeholder="Name" value={a.name || ''}
                            onChange={(e) => { const arr = [...form.activities]; arr[i] = {...a, name: e.target.value}; setForm({...form, activities: arr}); }} />
                    <Input type="number" className="col-span-2" placeholder="min" value={a.duration_minutes || ''}
                            onChange={(e) => { const arr = [...form.activities]; arr[i] = {...a, duration_minutes: Number(e.target.value)}; setForm({...form, activities: arr}); }} />
                    <Input className="col-span-6" placeholder="Description" value={a.description || ''}
                            onChange={(e) => { const arr = [...form.activities]; arr[i] = {...a, description: e.target.value}; setForm({...form, activities: arr}); }} />
                    <Button size="sm" variant="ghost" className="col-span-1 text-rose-500"
                            onClick={() => setForm({...form, activities: form.activities.filter((_, j) => j !== i)})}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
                <Button size="sm" variant="outline"
                        onClick={() => setForm({...form, activities: [...(form.activities || []), {name: '', duration_minutes: 10, description: ''}]})}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add activity
                </Button>
              </div>
              <div>
                <Label className="text-xs">Assessment</Label>
                <textarea rows={2} className="w-full border rounded p-2 text-sm"
                          value={form.assessment || ''} onChange={(e) => setForm({...form, assessment: e.target.value})} />
              </div>
              <div>
                <Label className="text-xs">Homework</Label>
                <textarea rows={2} className="w-full border rounded p-2 text-sm"
                          value={form.homework || ''} onChange={(e) => setForm({...form, homework: e.target.value})} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Materials needed <span className="text-slate-400">(one per line)</span></Label>
                <textarea rows={2} className="w-full border rounded p-2 text-sm"
                          value={Array.isArray(form.materials_needed) ? form.materials_needed.join('\n') : (form.materials_needed || '')}
                          onChange={(e) => setForm({...form, materials_needed: lines(e.target.value)})} />
              </div>
            </div>

            <div className="flex items-center justify-between border-t pt-3">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                {form.ai_generated && <Badge className="bg-violet-100 text-violet-700 gap-1"><Sparkles className="h-3 w-3" /> AI-assisted</Badge>}
                {picked && <span>Updated {fmt(form.updated_at)}</span>}
              </div>
              <div className="flex gap-2">
                {picked && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => clone.mutate()} disabled={clone.isPending}>
                      <Copy className="h-3.5 w-3.5 mr-1" /> Clone
                    </Button>
                    <Button size="sm" variant="ghost" className="text-rose-600"
                            onClick={() => window.confirm('Delete this plan?') && del.mutate()}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                    </Button>
                  </>
                )}
                <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending || !form.title || !form.subject_id}
                        data-testid="lp-save">
                  <Save className="h-3.5 w-3.5 mr-1" /> {save.isPending ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TeacherLessonPlans;
